import React, { useEffect, useState } from "react";
import { FaEdit, FaHome, FaPlus, FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "../components/ConfirmDialog";
import { PermissionGuard } from "../components/PermissionGuard";
import CompactActionBar from "../components/CompactActionBar";
import SharedFilter from "../components/SharedFilter";
import { useHiddenMode } from "../contexts/HiddenModeContext";
import "../css/standard-components.css";
import { usePageTitle } from "../hooks/usePageTitle";
import bookingService, { Booking } from "../services/bookingService";
import { Project, projectService } from "../services/projectService";
import { unitService } from "../services/unitService";
import {
  formatCurrency,
  formatDate,
  formatHiddenIncome,
} from "../utils/formatters";

const Bookings: React.FC = () => {
  usePageTitle();
  const navigate = useNavigate();
  const { isHiddenModeActive } = useHiddenMode();

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  // Pagination and filters
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(50);
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [projectFilter, setProjectFilter] = useState("");
  const [wingFilter, setWingFilter] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [unitTypeFilter, setUnitTypeFilter] = useState("");
  const [paymentTermsFilter, setPaymentTermsFilter] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [mobileFilter, setMobileFilter] = useState("");
  const [bookingDateFrom, setBookingDateFrom] = useState("");
  const [bookingDateTo, setBookingDateTo] = useState("");
  const [wingOptions, setWingOptions] = useState<string[]>([]);
  
  // Applied filters (used for API call)
  const [appliedFilters, setAppliedFilters] = useState({
    project: "",
    wing: "",
    unit: "",
    unitType: "",
    paymentTerms: "",
    customer: "",
    mobile: "",
    bookingDateFrom: "",
    bookingDateTo: "",
  });
  
  const [statusFilter, setStatusFilter] = useState("active");

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadBookings();
  }, [currentPage, appliedFilters, statusFilter, recordsPerPage]);

  useEffect(() => {
    // Load wings whenever project changes
    const loadWings = async () => {
      try {
        if (projectFilter) {
          const wings = await unitService.getWingsByProject(parseInt(projectFilter));
          setWingOptions(wings);
        } else {
          setWingOptions([]);
        }
      } catch (err) {
        setWingOptions([]);
      }
    };
    loadWings();
  }, [projectFilter]);

  const loadInitialData = async () => {
    try {
      const projectsData = await projectService.getProjects();
      setProjects(projectsData);
      
      // Auto-select first project
      if (projectsData.length > 0) {
        const firstProjectId = projectsData[0].id.toString();
        setProjectFilter(firstProjectId);
        setAppliedFilters(prev => ({ ...prev, project: firstProjectId }));
      }
    } catch (err) {
      setError("Failed to load initial data");
    }
  };

  const loadBookings = async () => {
    try {
      setLoading(true);

      // Parse unit filter to extract wing if format is "G-301"
      let wingParam = appliedFilters.wing;
      let unitParam = appliedFilters.unit;
      
      if (appliedFilters.unit && appliedFilters.unit.includes('-')) {
        const parts = appliedFilters.unit.split('-');
        if (parts.length === 2) {
          wingParam = parts[0].toUpperCase();
          unitParam = parts[1];
        }
      }

      const data = await bookingService.getBookings({
        page: currentPage,
        limit: recordsPerPage,
        projectId: appliedFilters.project ? parseInt(appliedFilters.project) : undefined,
        status: statusFilter || undefined,
        wing: wingParam || undefined,
        unitType: appliedFilters.unitType || undefined,
        memberName: appliedFilters.customer || undefined,
        mobile: appliedFilters.mobile || undefined,
        unitNo: unitParam ? unitParam.toUpperCase() : undefined,
        bookingDateFrom: appliedFilters.bookingDateFrom || undefined,
        bookingDateTo: appliedFilters.bookingDateTo || undefined,
      });

      // Apply client-side filter for payment terms if needed
      const filtered = appliedFilters.paymentTerms
        ? data.bookings.filter((b) => getPaymentTermsLabel(b).toLowerCase() === appliedFilters.paymentTerms.toLowerCase())
        : data.bookings;

      setBookings(filtered);
      setTotalPages(data.pagination.totalPages);
      setTotalRecords(data.pagination.total);
    } catch (err) {
      console.error("❌ Error loading bookings:", err);
      setError("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string): string => {
    switch (status) {
      case "active":
        return "status-active";
      case "completed":
        return "status-completed";
      case "cancelled":
        return "status-cancelled";
      default:
        return "status-active";
    }
  };

  const getUnitStatusDisplay = (unitStatus: string) => {
    switch (unitStatus) {
      case "approval_pending":
        return { text: "PENDING APPROVAL", className: "unit-status-pending" };
      case "sold":
        return { text: "APPROVED", className: "unit-status-approved" };
      case "unsold":
        return { text: "AVAILABLE", className: "unit-status-available" };
      case "hold":
        return { text: "ON HOLD", className: "unit-status-hold" };
      default:
        return {
          text: unitStatus.toUpperCase(),
          className: "unit-status-default",
        };
    }
  };

  const handleRecordsPerPageChange = (newLimit: number) => {
    setRecordsPerPage(newLimit);
    setCurrentPage(1); // Reset to first page when changing records per page
  };

  const getPaymentTermsLabel = (booking: Booking): string => {
    if (booking.paymentTerms) {
      const terms = booking.paymentTerms.toLowerCase();
      if (terms.includes("mixed")) return "Mixed Payment";
      const hasDown = terms.includes("down") || terms.includes("token");
      const hasLoan = terms.includes("loan");
      const hasInst = terms.includes("install") || terms.includes("construction_linked");
      const count = [hasDown, hasLoan, hasInst].filter(Boolean).length;
      if (count > 1) return "Mixed Payment";
      if (hasDown) return "Down Payment";
      if (hasLoan) return "Loan";
      if (hasInst) return "Installments";
    }
    // Derive from payment plans
    const types = new Set((booking.paymentPlans || []).map(p => p.planType));
    if (types.size === 0) return "";
    const hasDown = types.has("down_payment") || types.has("token");
    const hasLoan = types.has("loan");
    const hasInst = types.has("installment") || types.has("construction_linked");
    const count = [hasDown, hasLoan, hasInst].filter(Boolean).length;
    if (count > 1) return "Mixed Payment";
    if (hasDown) return "Down Payment";
    if (hasLoan) return "Loan";
    if (hasInst) return "Installments";
    return "";
  };

  return (
    <div className="page-container" style={{ background: '#fff' }}>
      {/* Compact Action Bar with everything in one line */}
      <CompactActionBar
        leftActions={
          <>
            <button 
              className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaSearch /> Filters
            </button>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>Status:</span>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="form-select"
                style={{ padding: '6px 12px', minWidth: '160px' }}
              >
                <option value="active">Active</option>
                <option value="cancelled">Cancelled</option>
                <option value="completed">Completed</option>
                <option value="rejected">Rejected</option>
                <option value="cancellation_pending">Cancellation Pending</option>
                <option value="">All</option>
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>Project:</span>
              <select
                value={projectFilter}
                onChange={(e) => {
                  const val = e.target.value;
                  setProjectFilter(val);
                  setAppliedFilters(prev => ({ ...prev, project: val }));
                  setCurrentPage(1);
                }}
                className="form-select"
                style={{ padding: '6px 12px', minWidth: '200px' }}
              >
                {projects.map((project) => (
                  <option key={project.id} value={project.id.toString()}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>Show:</span>
              <select
                value={recordsPerPage}
                onChange={(e) => handleRecordsPerPageChange(parseInt(e.target.value))}
                className="form-select"
                style={{ padding: '6px 12px', minWidth: '100px' }}
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={150}>150</option>
                <option value={200}>200</option>
              </select>
            </div>
          </>
        }
        rightActions={
          <PermissionGuard permission="booking.create">
            <button
              className="btn btn-primary"
              onClick={() => navigate("/booking/add")}
            >
              <FaPlus /> Add Booking
            </button>
          </PermissionGuard>
        }
      />

      {error && (
        <div
          style={{
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#dc2626",
            padding: "12px 20px",
            margin: "0",
            position: "relative",
          }}
        >
          {error}
          <button
            onClick={() => setError("")}
            style={{
              position: "absolute",
              right: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
              color: "#dc2626",
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Shared Filter Component - Collapsible */}
      {showFilters && (
        <SharedFilter
          show={true}
          unitNumber={unitFilter}
          customerName={customerFilter}
          mobile={mobileFilter}
          onUnitNumberChange={setUnitFilter}
          onCustomerNameChange={setCustomerFilter}
          onMobileChange={setMobileFilter}
          unitType={unitTypeFilter}
          onUnitTypeChange={setUnitTypeFilter}
          paymentTerms={paymentTermsFilter}
          onPaymentTermsChange={setPaymentTermsFilter}
          dateFrom={bookingDateFrom}
          dateTo={bookingDateTo}
          onDateFromChange={setBookingDateFrom}
          onDateToChange={setBookingDateTo}
          dateLabel="Booking Date"
          unitNumberPlaceholder="301 or G-301"
          onApply={() => {
            setAppliedFilters({
              project: projectFilter,
              wing: wingFilter,
              unit: unitFilter,
              unitType: unitTypeFilter,
              paymentTerms: paymentTermsFilter,
              customer: customerFilter,
              mobile: mobileFilter,
              bookingDateFrom: bookingDateFrom,
              bookingDateTo: bookingDateTo,
            });
            setCurrentPage(1);
          }}
          onClear={() => {
            setProjectFilter("");
            setWingFilter("");
            setUnitFilter("");
            setUnitTypeFilter("");
            setPaymentTermsFilter("");
            setCustomerFilter("");
            setMobileFilter("");
            setBookingDateFrom("");
            setBookingDateTo("");
            setAppliedFilters({
              project: "",
              wing: "",
              unit: "",
              unitType: "",
              paymentTerms: "",
              customer: "",
              mobile: "",
              bookingDateFrom: "",
              bookingDateTo: "",
            });
            setCurrentPage(1);
          }}
        />
      )}

      {/* Table Section - No padding, direct alignment */}
      <div style={{ background: '#fff', margin: 0, padding: 0 }}>
        {loading ? (
          <div
            style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}
          >
            Loading bookings...
          </div>
        ) : (
          <table className="standard-table" style={{ margin: 0 }}>
            <thead>
              <tr>
                <th>Sr. No</th>
                <th>Project</th>
                <th>Unit No</th>
                <th>Unit Type</th>
                <th>Unit Size</th>
                <th>Customer Name & Contact Number</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th>Payment Terms</th>
                <th>Booking Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((booking, index) => {
                const primaryApplicant =
                  booking.applicants.find((a) => a.isPrimary) ||
                  booking.applicants[0];
                const serialNumber = totalRecords - (currentPage - 1) * recordsPerPage - index;
                return (
                  <tr key={booking.id}>
                    <td style={{ fontWeight: "600", color: "#4f46e5" }}>
                      {serialNumber}
                    </td>
                    <td>
                      {booking.unit?.project?.name ||
                        booking.project?.name ||
                        "N/A"}
                    </td>
                    <td>
                      <div style={{ fontWeight: "500" }}>
                        {booking.unit.wing
                          ? `${booking.unit.wing}-${booking.unit.unitNo}`
                          : booking.unit.unitNo}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: "13px", color: "#374151" }}>
                        {booking.unit.unitType || "-"}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: "13px", color: "#6b7280" }}>
                        {booking.unit.size || "-"}
                      </div>
                    </td>
                    <td>
                      <div>
                        <div style={{ fontWeight: "500", color: "#374151" }}>
                          {primaryApplicant?.firstName}{" "}
                          {primaryApplicant?.surname}
                        </div>
                        <div style={{ fontSize: "12px", color: "#6b7280" }}>
                          {primaryApplicant?.contactNumber}
                        </div>
                      </div>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: "500", color: "#059669" }}>
                        ₹{(booking.totalAmount || 0).toLocaleString('en-IN')}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: "13px", color: "#374151" }}>
                        {getPaymentTermsLabel(booking) || "-"}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: "13px", color: "#6b7280" }}>
                        {new Date(booking.bookingDate).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </div>
                    </td>
                    <td>
                      <div className="table-actions">
                        {booking.status === "active" && (
                          <PermissionGuard permission="booking.edit">
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() =>
                                navigate(`/booking/edit?id=${booking.id}`)
                              }
                              title="Edit"
                            >
                              <FaEdit />
                            </button>
                          </PermissionGuard>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {bookings.length === 0 && !loading && (
          <div
            style={{ textAlign: "center", padding: "60px", color: "#6b7280" }}
          >
            <FaHome
              style={{ fontSize: "48px", marginBottom: "16px", opacity: "0.5" }}
            />
            <p style={{ fontSize: "16px", margin: 0 }}>No bookings found</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {bookings.length > 0 && (
        <div
          className="content-section"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ color: "#6b7280", fontSize: "14px" }}>
            {totalPages > 1
              ? `Showing ${
                  (currentPage - 1) * recordsPerPage + 1
                } to ${Math.min(
                  currentPage * recordsPerPage,
                  (currentPage - 1) * recordsPerPage + bookings.length
                )} records`
              : `Showing ${bookings.length} record${
                  bookings.length !== 1 ? "s" : ""
                }`}
          </div>

          {totalPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <button
                className="btn btn-secondary"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>

              <span style={{ color: "#6b7280", fontSize: "14px" }}>
                Page {currentPage} of {totalPages}
              </span>

              <button
                className="btn btn-secondary"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() =>
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }))
        }
      />
    </div>
  );
};

export default Bookings;
